import React, { useState } from 'react';
import { Spinner } from '../ui';
import { useAuth } from '../../context/AuthContext';
import { evaluateAccess } from '../../context/authGating';
import Sidebar from './Sidebar';
import DashboardNavbar from './DashboardNavbar';
import AccountStatusGate from './AccountStatusGate';
import { Outlet } from 'react-router-dom';
import { LanguageNavigate } from '../../hooks/useLanguageNavigate';
import SEO from '../common/SEO';

const AppLayout: React.FC = () => {
  const { currentUser, loading } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = React.useCallback(() => {
    setMobileOpen(prev => !prev);
  }, []);

  const handleDrawerClose = React.useCallback(() => {
    setMobileOpen(false);
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner size={40} />
      </div>
    );
  }

  if (!currentUser) {
    return <LanguageNavigate to="/login" replace />;
  }

  // Central app-shell gate: an unverified email, a company still awaiting admin
  // approval, a blocked company, or a missing company document (no tenant) must
  // not reach the protected shell. Admins use a separate DB-based auth flow and
  // never pass through AppLayout. `loading` is already handled above, so an absent
  // companyData here means it is genuinely missing — not still loading.
  const access = evaluateAccess({
    emailVerified: currentUser.emailVerified,
    companyData: currentUser.companyData,
  });
  if (access !== 'allow') {
    return <AccountStatusGate decision={access} status={currentUser.companyData?.status} />;
  }

  return (
    <>
      <SEO noIndex={true} />
      {/* Fixed background */}
      <div className="fixed inset-0 z-0 bg-gradient-to-b from-canvas via-surface-2 to-canvas">
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_30%_20%,rgb(var(--color-primary)/0.08)_0%,transparent_50%)] dark:bg-[radial-gradient(circle_at_30%_20%,rgb(var(--color-primary)/0.15)_0%,transparent_50%)]" />
      </div>

      {/* Mobile Navbar */}
      <DashboardNavbar onMenuClick={handleDrawerToggle} />

      {/* Scrollable content */}
      <div className="relative z-[1] flex min-h-screen pt-16 md:pt-0">
        <Sidebar mobileOpen={mobileOpen} onMobileClose={handleDrawerClose} />
        <main className="flex-grow p-0 relative w-full overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </>
  );
};

export default AppLayout;
